import { useEffect, useRef, type RefObject } from 'react';

type Options = {
  /** Scroll container — receives touch events and drives scrollLeft/scrollTop. */
  container: RefObject<HTMLDivElement>;
  /** Wrapper whose size is `baseWidth * zoom` × `baseHeight * zoom`. Resized
   *  imperatively during the gesture so scroll bounds stay correct. */
  outer: RefObject<HTMLDivElement>;
  /** Inner canvas, gets `transform: scale(zoom)` applied imperatively. */
  inner: RefObject<HTMLDivElement>;
  baseWidth: number;
  baseHeight: number;
  zoom: number;
  /** Called once at gesture end with the final zoom value, to commit to React
   *  state so the next render keeps the same visual scale. */
  setZoom: (z: number) => void;
  minZoom: number;
  maxZoom: number;
  /** Optional flag exposed so other handlers (e.g. drag) can skip work while
   *  the user is pinching with two fingers. */
  pinchingRef?: RefObject<{ current: boolean }>;
};

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Two-finger pinch-to-zoom + pan, native-feeling: during the gesture we mutate
 * the DOM directly (transform on the inner element, width/height on the outer
 * wrapper, scroll on the container) so motion is smooth at touch-frame rate.
 * React state is only updated on touchend so the new value persists into the
 * next render — no jumps, no resets between fingers.
 *
 * The math per frame is the standard one-pass pinch-pan:
 *   canvasPt = (prevMid + prevScroll) / prevZoom
 *   newZoom  = clamp(prevZoom * (newDist / prevDist))
 *   newScroll = canvasPt * newZoom − newMid
 * which anchors the canvas point that was under the previous midpoint to the
 * current midpoint, combining pan and zoom into a single update.
 */
export function usePinchZoom({
  container,
  outer,
  inner,
  baseWidth,
  baseHeight,
  zoom,
  setZoom,
  minZoom,
  maxZoom,
  pinchingRef,
}: Options) {
  // Mirror props in refs so the long-lived listener always reads fresh values
  // without re-binding.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const minRef = useRef(minZoom);
  minRef.current = minZoom;
  const maxRef = useRef(maxZoom);
  maxRef.current = maxZoom;
  const baseWRef = useRef(baseWidth);
  baseWRef.current = baseWidth;
  const baseHRef = useRef(baseHeight);
  baseHRef.current = baseHeight;

  useEffect(() => {
    const el = container.current;
    if (!el) return;

    let active = false;
    let liveZoom = zoomRef.current;
    let prevDist = 0;
    let prevMidX = 0;
    let prevMidY = 0;

    function dist(t: TouchList) {
      const dx = t[0].clientX - t[1].clientX;
      const dy = t[0].clientY - t[1].clientY;
      return Math.hypot(dx, dy);
    }

    function applyDom(z: number) {
      const i = inner.current;
      const o = outer.current;
      if (i) i.style.transform = `scale(${z})`;
      if (o) {
        o.style.width = `${baseWRef.current * z}px`;
        o.style.height = `${baseHRef.current * z}px`;
      }
    }

    function onStart(e: TouchEvent) {
      if (e.touches.length !== 2) return;
      active = true;
      liveZoom = zoomRef.current;
      prevDist = dist(e.touches);
      prevMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      prevMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      if (pinchingRef?.current) pinchingRef.current.current = true;
    }

    function onMove(e: TouchEvent) {
      if (!active || e.touches.length !== 2 || !el) return;
      e.preventDefault();

      const newDist = dist(e.touches);
      const newMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const newMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

      const rect = el.getBoundingClientRect();
      const prevLocalX = prevMidX - rect.left;
      const prevLocalY = prevMidY - rect.top;
      const newLocalX = newMidX - rect.left;
      const newLocalY = newMidY - rect.top;

      // Canvas point under PREVIOUS midpoint, in PREVIOUS zoom.
      const canvasX = (prevLocalX + el.scrollLeft) / liveZoom;
      const canvasY = (prevLocalY + el.scrollTop) / liveZoom;

      const scale = newDist / prevDist;
      const newZoom = clamp(liveZoom * scale, minRef.current, maxRef.current);

      // Apply visual scale FIRST so the scroll set below lands inside the new,
      // larger (or smaller) scroll bounds.
      applyDom(newZoom);
      el.scrollLeft = canvasX * newZoom - newLocalX;
      el.scrollTop = canvasY * newZoom - newLocalY;

      liveZoom = newZoom;
      prevDist = newDist;
      prevMidX = newMidX;
      prevMidY = newMidY;
    }

    function onEnd(e: TouchEvent) {
      if (e.touches.length >= 2) return;
      if (!active) return;
      active = false;
      // Commit final value into React state. The next render will produce the
      // exact same width/height/transform we already set imperatively, so
      // there's no visual jump.
      const final = liveZoom;
      setZoom(final);
      if (pinchingRef?.current) {
        const ref = pinchingRef.current;
        setTimeout(() => { ref.current = false; }, 80);
      }
    }

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [container, outer, inner, setZoom, pinchingRef]);
}
